<script setup>
import { deleteRequest, getRequest, postRequest } from "@/utils/request.js";
import {
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
} from "@ant-design/icons-vue";
import { message } from "ant-design-vue";
import { h, onMounted, reactive, ref, unref } from "vue";
import QuartzFormModal from "./modules/QuartzFormModal.vue";

const formRef = ref();
const searchFormModel = ref({});
const datasource = ref([]);
const loading = ref(false);
const modalRef = ref();
const columns = [
  {
    key: "name",
    dataIndex: "name",
    title: "任务名称",
    width: "15%",
  },
  {
    key: "jobClassName",
    dataIndex: "jobClassName",
    title: "job类名",
    width: "15%",
  },
  {
    key: "cron",
    dataIndex: "cron",
    title: "cron",
    width: "15%",
  },
  {
    key: "parameter",
    dataIndex: "parameter",
    title: "参数",
    width: "15%",
  },
  {
    key: "status",
    dataIndex: "status",
    title: "状态",
    width: "15%",
  },
  {
    key: "action",
    dataIndex: "action",
    title: "操作",
    width: "300px",
  },
];

const pagination = reactive({
  current: 1,
  pageSize: 10,
  pageSizeOptions: ["10", "50", "100"],
  showTotal: (total, range) => {
    return range[0] + "-" + range[1] + " 共" + total + "条";
  },
  showQuickJumper: true,
  showSizeChanger: true,
  total: 0,
});

const loadData = () => {
  loading.value = true;
  const { current, pageSize } = unref(pagination);
  const requestBody = Object.assign({}, unref(searchFormModel), {
    pageNo: current,
    pageSize,
  });
  getRequest("/quartz", requestBody)
    .then((response) => {
      if (response.success) {
        datasource.value = response.data.records;
      }
    })
    .finally(() => {
      loading.value = false;
    });
};

const resetForm = () => {
  formRef.value.resetFields();
  loadData();
};

const handleAdd = (record) => {
  modalRef.value.title = "任务新增";
  modalRef.value.handleAdd(record);
};

const handleEdit = (record) => {
  modalRef.value.title = "任务编辑";
  modalRef.value.handleEdit(record);
};

const handleExecute = (record) => {
  postRequest(`/quartz/execute/${record.id}`).then((response) => {
    if (response.success) {
      message.success("执行成功");
      loadData();
    } else {
      message.error("执行失败。" + response.message);
    }
  });
};

const handleResume = (record) => {
  postRequest(`/quartz/resume/${record.id}`).then((response) => {
    if (response.success) {
      message.success("恢复成功");
      loadData();
    } else {
      message.error("恢复失败。" + response.message);
    }
  });
};

const handlePause = (record) => {
  postRequest(`/quartz/pause/${record.id}`).then((response) => {
    if (response.success) {
      message.success("暂停成功");
      loadData();
    } else {
      message.error("暂停失败。" + response.message);
    }
  });
};

const handleDelete = (record) => {
  deleteRequest(`/quartz/${record.id}`).then((response) => {
    if (response.success) {
      message.success("删除成功");
      loadData();
    } else {
      message.error("删除失败。" + response.message);
    }
  });
};

const tableChange = ({ current, pageSize }) => {
  pagination.current = current;
  pagination.pageSize = pageSize;
  loadData();
};

onMounted(() => {
  loadData();
});
</script>

<template>
  <div>
    <a-card>
      <a-form
        ref="formRef"
        layout="inline"
        :labelCol="{ span: 3, offset: 1 }"
        :model="searchFormModel"
        @keydown.enter="loadData"
      >
        <a-row :gutter="[10, 10]" style="width: 100%">
          <a-col :span="6">
            <a-form-item label="任务名称" name="name">
              <a-input
                v-model:value="searchFormModel.name"
                placeholder="请输入任务名称"
              />
            </a-form-item>
          </a-col>
          <a-col :span="24">
            <a-button
              type="primary"
              style="margin-left: 18px"
              :icon="h(SearchOutlined)"
              @click="loadData"
              @keydown.enter="loadData"
            >
              查询
            </a-button>
            <a-button
              style="margin-left: 18px"
              :icon="h(ReloadOutlined)"
              @click="resetForm"
              >重置
            </a-button>
          </a-col>
        </a-row>
      </a-form>
    </a-card>

    <a-card style="margin-top: 10px">
      <div style="margin-bottom: 12px">
        <a-button type="primary" @click="handleAdd" :icon="h(PlusOutlined)"
          >新增
        </a-button>
      </div>
      <a-table
        row-key="id"
        :loading="loading"
        :columns="columns"
        :data-source="datasource"
        :bordered="true"
        :pagination="pagination"
        @change="tableChange"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.dataIndex === 'status'">
            <template v-if="record[column.dataIndex]">
              <a-tag color="green">启用</a-tag>
            </template>
            <template v-else>
              <a-tag color="red">停用</a-tag>
            </template>
          </template>

          <template v-if="column.dataIndex === 'action'">
            <a type="link" @click="handleEdit(record)">编辑</a>
            <a-divider type="vertical" />
            <a-popconfirm title="确认要删除吗?" @confirm="handleDelete(record)">
              <a type="link">删除</a>
            </a-popconfirm>
            <a-divider type="vertical" />
            <a-popconfirm
              title="确认要立即执行吗?"
              @confirm="handleExecute(record)"
            >
              <a type="link">立即执行</a>
            </a-popconfirm>
            <template v-if="record.status === true">
              <a-divider type="vertical" />
              <a type="link" @click="handlePause(record)">停止</a>
            </template>
            <template v-else>
              <a-divider type="vertical" />
              <a type="link" @click="handleResume(record)">恢复</a>
            </template>
          </template>
        </template>
      </a-table>
    </a-card>
    <!--    <role-form-modal ref="modalRef" @ok="loadData" />-->
    <!--    <permission-form-modal ref="modalRef" @ok="loadData" />-->
    <quartz-form-modal ref="modalRef" @ok="loadData" />
  </div>
</template>

<style scoped lang="scss"></style>
